'use client';

import { Button } from '@nextui-org/react';

import {
  useCreateReviewMutation,
  useUploadReviewPhotosMutation,
} from '@/entities/review/query';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormTextArea } from '@/components/form/form-textarea';
import { Form } from '@/components/ui/form';
import { FormRate } from '@/components/form/form-rate';
import { FormFileUpload } from '@/components/form/form-file-upload';
import { FormInput } from '@/components/form/form-input';
import {
  FormConsentCheckbox,
  personalDataConsentSchema,
} from '@/components/form/form-consent-checkbox';
import { useToast } from '@/components/ui/use-toast';
import { AxiosError } from 'axios';

const reviewFormSchema = z.object({
  authorName: z
    .string({ required_error: 'Укажите имя' })
    .min(2, 'Минимум 2 символа')
    .max(120),
  promoEmail: z
    .string({ required_error: 'Укажите email' })
    .email('Некорректный email'),
  description: z
    .string({
      required_error: 'Напишите текст отзыва',
    })
    .min(1),
  rate: z
    .number({
      required_error: 'Оцените отзыв',
    })
    .min(1)
    .max(5),
  images: z.array(z.any()).max(5, 'Не больше 5 фото').optional(),
  personalDataConsent: personalDataConsentSchema,
});

export const ReviewForm = ({
  masterclassId,
  masterclassWordId,
  afterSubmitSuccess = 'push',
}: {
  masterclassId: number;
  masterclassWordId: string;
  /** `back` — закрыть bottom sheet (intercept add-review) */
  afterSubmitSuccess?: 'push' | 'back';
}) => {
  const { toast } = useToast();
  const { mutate, isPending } = useCreateReviewMutation(
    masterclassId,
    masterclassWordId,
    { afterSuccess: afterSubmitSuccess },
  );
  const reviewForm = useForm<z.infer<typeof reviewFormSchema>>({
    resolver: zodResolver(reviewFormSchema),
  });

  const { handleSubmit } = reviewForm;
  const { mutateAsync: uploadPhotos, isPending: isUploading } =
    useUploadReviewPhotosMutation();

  const onSubmit = async (values: z.infer<typeof reviewFormSchema>) => {
    /** Галочка согласия — только для UI, на бэкенд не отправляется. */
    const { personalDataConsent: _personalDataConsent, ...reviewValues } =
      values;
    try {
      let uploadedImagesUrls: string[] = [];
      if (reviewValues.images?.length) {
        const res = await uploadPhotos(reviewValues.images as File[]);
        const raw = res.data as unknown;
        const list = Array.isArray(raw)
          ? raw
          : (raw as { data?: Array<{ url?: string }> }).data ?? [];
        uploadedImagesUrls = list.map((f) => f.url).filter(Boolean) as string[];
      }
      mutate({
        ...reviewValues,
        masterclassId: Number(masterclassId),
        images: uploadedImagesUrls,
      });
    } catch (e) {
      console.error('error', e);
      const ax = e as AxiosError<{ error?: { message?: string } }>;
      const msg =
        ax.response?.data?.error?.message ??
        (typeof ax.response?.data === 'string' ? ax.response.data : null) ??
        'Не удалось загрузить фото';
      toast({ title: msg, variant: 'error' });
    }
  };

  return (
    <>
      <p className="text-sm text-default-600 max-w-[500px] w-full">
        После одобрения модератором отзыв появится на
        сайте, а на указанный email — промокод на 10% скидки. При отклонении
        пришлём письмо с причиной.
      </p>
      <Form {...reviewForm}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3 max-w-[500px] w-full"
        >
          <FormInput
            formReactHookForm={reviewForm}
            name="authorName"
            label="Имя в отзыве"
            placeholder="Как будет видно другим"
            isRequired
            labelPlacement="outside"
          />
          <FormInput
            formReactHookForm={reviewForm}
            name="promoEmail"
            label="Email для промокода"
            placeholder="На этот адрес отправим код"
            type="email"
            autoComplete="email"
            isRequired
            labelPlacement="outside"
          />
          <FormRate
            formReactHookForm={reviewForm}
            name="rate"
            isRequired
            label="Оценка"
          />
          <FormTextArea
            labelPlacement="outside"
            minRows={5}
            formReactHookForm={reviewForm}
            name="description"
            isRequired
            label="Текст отзыва"
            placeholder="..."
          />
          <div className="space-y-1.5 w-full">
            <FormFileUpload
              maxSize={10 * 1024 * 1024}
              maxFileCount={5}
              formReactHookForm={reviewForm}
              label="Фотографии"
              name="images"
              isModal
              multiple
            />
            <p className="text-xs text-default-500 leading-relaxed">
              До 5 фото, до 10 МБ каждое. Форматы: JPEG, PNG, WebP, GIF (и
              другие изображения).
            </p>
          </div>
          <FormConsentCheckbox
            formReactHookForm={reviewForm}
            name="personalDataConsent"
          />
          <div className="flex mt-4 flex-col gap-2">
            <Button
              isLoading={isPending || isUploading}
              color="primary"
              type="submit"
              size="lg"
            >
              Отправить
            </Button>
          </div>
        </form>
        {reviewForm.formState.errors && (
          <div className="text-destructive text-sm">
            {Object.values(reviewForm.formState.errors).map((error) => (
              <p key={error.message}>{error.message}</p>
            ))}
          </div>
        )}
      </Form>
    </>
  );
};
